import React, { useCallback, useMemo } from "react";
import { createEditor } from "slate";
import { Slate, Editable, withReact } from "slate-react";
import { StyleSheetManager } from "styled-components";
import { RichTextEditor } from "@pearsontechnology/aero-components-v2";
import { StageElement, type ViewerDeviceRuntime } from "../../../../../types";
import ImageElement from "../../../image";
import { SectionComponent } from "../../../stage/Section/Section";
import { StageElementWrapper } from "../../../stage/StageElement/StageElementWrapper";

// Import text element plugins (same subset as TableElementViewer —
// excludes KeyTerm & Footnote to avoid circular dependencies)
import InlineIcon from "../InlineIcon";
import Link from "../Link/Plugin";
import ReferenceLink from "../ReferenceLink/Plugin";
import MathMLExpressionDefault from "../MathMLExpressionDefault";
import CollapsibleText from "../CollapsibleText";
import ToolTip from "../PopupTip";
import LegacyInline from "../LegacyInline/Plugin";
import type { Media } from "@bronte/viewer";
import type { InlineIconConfig } from "../InlineIcon/DefaultConfig";
import { normalizeInitialValue } from "../../../../../util/normalizeSlateValue";

/**
 * Renders a single text stage-element using Slate Editor + plugins,
 * following the same pattern as CellContent in TableElementViewer.
 */
const PopoverTextElement: React.FC<{
  element: StageElement & { value?: any };
  viewerDevice?: ViewerDeviceRuntime;
}> = ({ element, viewerDevice }) => {
  const config = (element.config ?? {}) as { pluginConfigs?: Record<string, InlineIconConfig> };
  const pluginConfigs = config.pluginConfigs;

  const plugins = [
    RichTextEditor.Plugins.BoldItalicUnderline.Plugin(),
    RichTextEditor.Plugins.CustomParagraph.Plugin(),
    RichTextEditor.Plugins.AutoInsertLists.Plugin(),
    RichTextEditor.Plugins.Headings.Plugin(),
    RichTextEditor.Plugins.Paragraph.Plugin(),
    RichTextEditor.Plugins.StyledText.Plugin(true),
    ToolTip.Plugin(),
    InlineIcon.Plugin({ pluginConfigs, viewerDevice }),
    Link(),
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    ReferenceLink(() => {}),
    MathMLExpressionDefault.Plugin(),
    CollapsibleText.Plugin(),
    LegacyInline(),
  ];

  const editor = useMemo(() => {
    let baseEditor = withReact(createEditor());
    plugins.forEach((plugin) => {
      baseEditor = plugin(baseEditor);
    });
    return baseEditor;
  }, [viewerDevice]);

  const normalizedValue = useMemo(
    () => normalizeInitialValue(editor, element.value || []),
    [editor, element.value],
  );

  const renderElement = useCallback((props: any) => {
    for (const plugin of plugins) {
      if (plugin.renderElement) {
        const result = plugin.renderElement(props);
        if (result) return result;
      }
    }
    const { attributes, children } = props;
    return <div {...attributes}>{children}</div>;
  }, []);

  const renderLeaf = useCallback((props: any) => {
    let { children } = props;
    if (props.leaf.bold) children = <strong>{children}</strong>;
    if (props.leaf.italic) children = <em>{children}</em>;
    if (props.leaf.underline) children = <u>{children}</u>;
    if (props.leaf.code) children = <code>{children}</code>;
    if (props.leaf.strikethrough) children = <s>{children}</s>;

    for (const plugin of plugins) {
      if (plugin.renderLeaf) {
        const result = plugin.renderLeaf({ ...props, children });
        if (result !== undefined) children = result;
      }
    }
    return <span {...props.attributes}>{children}</span>;
  }, []);

  return (
    <Slate editor={editor} initialValue={normalizedValue}>
      <Editable readOnly={true} renderElement={renderElement} renderLeaf={renderLeaf} />
    </Slate>
  );
};

/**
 * Render a single stage element inside popover content, wrapped with
 * StageElementWrapper for proper spacing (same as ViewerComponent does).
 * Handles text and image elements; other types are silently skipped.
 */
const renderPopoverElement = (
  element: StageElement & { value?: any },
  key: string,
  onMediaClick?: (data: Media) => void,
  viewerDevice?: ViewerDeviceRuntime,
): JSX.Element | null => {
  if (!element.type) return null;

  let elementComponent: JSX.Element | null = null;

  switch (element.type) {
    case "stage-element-text":
      elementComponent = (
        <PopoverTextElement key={key} element={element} viewerDevice={viewerDevice} />
      );
      break;
    case "stage-element-image":
      elementComponent = (
        <ImageElement
          key={key}
          element={element}
          readOnly={true}
          onMediaClick={onMediaClick}
          viewerDevice={viewerDevice}
        />
      );
      break;
    default:
      return null;
  }

  if (!elementComponent) return null;

  // Wrap with StageElementWrapper for proper padding/spacing
  return (
    <StageElementWrapper
      key={key}
      element={element}
      elementIndex={key}
      elementsPadding={{ vertical: 10, horizontal: 10 }}
    >
      {elementComponent}
    </StageElementWrapper>
  );
};

interface PopoverStageContentProps {
  stage: {
    sections?: Array<{
      rows?: Array<{
        columns?: Array<{
          stageElements?: (StageElement & { value?: any })[];
        }>;
      }>;
    }>;
  };
  onMediaClick?: (data: Media) => void;
  viewerDevice?: ViewerDeviceRuntime;
}

/**
 * Self-contained stage renderer for popover content (Footnote / KeyTerm).
 * Uses SectionComponent for proper stage layout (sections → rows → columns)
 * with a local renderStageElement that handles text & image elements
 * (similar to TableElementViewer's CellContent), without depending on
 * ViewerComponent.
 */
export const PopoverStageContent: React.FC<PopoverStageContentProps> = ({
  stage,
  onMediaClick,
  viewerDevice,
}) => {
  if (!stage?.sections) {
    return <div>Content not available</div>;
  }

  return (
    <StyleSheetManager target={document.head}>
      <>
        {stage.sections.map((section, sectionIndex) => (
          <SectionComponent
            key={`popover-section-${sectionIndex}`}
            section={section}
            viewerDevice={viewerDevice}
            renderStageElement={(element, elementIndex) =>
              renderPopoverElement(
                element as StageElement & { value?: any },
                element.ref || `element-${elementIndex}`,
                onMediaClick,
                viewerDevice,
              )
            }
          />
        ))}
      </>
    </StyleSheetManager>
  );
};
